/**
 * Shared parsing + row mapping for deposit history
 * (`GET /v1/wallet/deposits` and the asset Deposit page list).
 */
import { depositRowPresentation } from "./depositDeclarationModel";

/** Clicks on these targets should not open the deposit-history detail modal (row/card is otherwise clickable). */
export function orderHistoryClickIgnoreDetail(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("button, a[href]"));
}

export function extractDepositHistoryList(res) {
  if (Array.isArray(res)) return res;
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.deposits)) return d.deposits;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.rows)) return d.rows;
  if (d && Array.isArray(d.transactions)) return d.transactions;
  if (d && Array.isArray(d.list)) return d.list;
  return [];
}

/** Optional `total` / `count` when `data` is an object wrapper around the list array. */
export function depositHistoryApiParse(res) {
  if (res && res.success === false) return { rawRows: [], total: null };
  const rawRows = extractDepositHistoryList(res);
  const d = res?.data;
  let total = null;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const rawTotal = d.total ?? d.totalCount ?? d.count ?? d.total_count;
    const n = typeof rawTotal === "string" ? Number(rawTotal) : rawTotal;
    if (Number.isFinite(n)) total = n;
  }
  return { rawRows, total };
}

export function formatDepHistoryDate(v) {
  if (v == null || v === "") return "—";
  try {
    const t = new Date(v);
    if (Number.isNaN(t.getTime())) return String(v);
    return t.toLocaleString();
  } catch {
    return String(v);
  }
}

export function truncateMid(s, headLen = 8, tailLen = 4) {
  if (s == null || s === "—" || s === "") return "—";
  const str = String(s);
  if (str.length <= headLen + tailLen + 1) return str;
  return `${str.slice(0, headLen)}…${str.slice(-tailLen)}`;
}

export function normalizeHistoryStatusLabel(raw) {
  if (raw == null) return "—";
  const t = String(raw).trim();
  if (!t) return "—";
  return t;
}

export function historyStatusTone(label) {
  const t = String(label || "").toLowerCase();
  if (!t || t === "—") return "neutral";
  if (/(success|completed|credited|confirm|confirmed|done|approve|approved)/i.test(t)) return "success";
  if (/(pending|processing|in progress|confirming|queued|wait)/i.test(t)) return "pending";
  if (/(fail|failed|reject|rejected|rejcted|cancel|canceled|error|expired)/i.test(t)) return "danger";
  return "info";
}

const FIAT_TICKERS = new Set(["INR", "USD", "EUR", "AED", "GBP", "TRY", "BRL"]);

/** For Orders filters (`Crypto` | `Fiat`) — uses API fields when present, else ticker heuristic. */
export function inferDepositHistoryKind(r, shortSymbol) {
  const t = String(r.type || r.deposit_type || r.asset_type || "").toLowerCase();
  if (t === "fiat" || r.is_fiat === true || r.fiat === true) return "Fiat";
  if (t === "crypto" || t === "coin") return "Crypto";
  const sym = String(shortSymbol || r.short_name || r.currency || r.coin || "").toUpperCase();
  if (sym && FIAT_TICKERS.has(sym)) return "Fiat";
  return "Crypto";
}

export function mapDepHistoryRow(r, i) {
  const tx = r.transaction_hash || r.txid || r.txId || r.tx_id || r.hash;
  const addr = r.address || r.to_address || r.destAddress || r.destinationAddress;
  const rawStatus = r.status == null ? "" : String(r.status).trim();
  const rawDeclaration = r.declaration_status == null ? "" : String(r.declaration_status).trim();
  const presentation = depositRowPresentation(rawStatus, rawDeclaration);
  const actionLabel = presentation
    ? presentation.label
    : normalizeHistoryStatusLabel(rawStatus || r.transaction_status || r.action || "—");
  const chainFullName =
    r.chain_full_name ||
    r.chainFullName ||
    r.chain_full ||
    r.chainName ||
    r.network_full_name ||
    (r.metadata && (r.metadata.chain_full_name || r.metadata.chainFullName || r.metadata.network_full_name)) ||
    null;
  const short =
    r.short_name ||
    r.shortName ||
    r.currency_short_name ||
    r.currency ||
    r.coin ||
    r.token ||
    (r.currency_id && (r.currency_id.short_name || r.currency_id.symbol)) ||
    "—";
  const currency = r.currency || r.coin || r.token || r.asset || r.name || short || "—";
  const netAmount =
    r.net_amount ??
    r.netAmount ??
    r.amount ??
    r.deposit_amount ??
    r.depositAmount ??
    (r.metadata && (r.metadata.net_amount ?? r.metadata.amount)) ??
    "—";
  const kind = inferDepositHistoryKind(r, short);
  return {
    id: r._id || r.id || tx || `row-${i}`,
    kind,
    date: formatDepHistoryDate(r.createdAt || r.created_at || r.time || r.date),
    network: r.chain || r.network || (r.metadata && r.metadata.chain) || "—",
    chain_full_name: chainFullName != null && String(chainFullName).trim() ? String(chainFullName) : "—",
    short_name: short != null && String(short).trim() ? String(short).toUpperCase() : "—",
    currency: currency != null && String(currency).trim() ? String(currency) : "—",
    net_amount: netAmount != null && netAmount !== "" ? String(netAmount) : "—",
    address: addr != null && addr !== "" ? addr : "—",
    addressShort: addr != null && addr !== "" ? truncateMid(addr, 10, 6) : "—",
    txid: tx != null && tx !== "" ? String(tx) : "—",
    txidShort: tx != null && tx !== "" ? truncateMid(String(tx), 10, 6) : "—",
    depositWallet: r.wallet || r.walletType || r.wallet_type || r.depositWallet || "Main Wallet",
    status: rawStatus,
    declaration_status: rawDeclaration,
    action: actionLabel,
    explorerLink: r.explorer || {},
    actionTone: presentation ? presentation.tone : historyStatusTone(actionLabel),
    needsSenderConfirm: Boolean(presentation && presentation.needsSenderConfirm),
  };
}

function pickDepositExplorerHref(raw) {
  if (raw == null) return null;
  const s = typeof raw === "string" ? raw.trim() : String(raw).trim();
  return s || null;
}

/** URL must come from API `explorer`; otherwise null (no link control). */
export function resolveDepositHistoryAddressExplorerHref(explorer, address) {
  const ex = explorer && typeof explorer === "object" ? explorer : {};
  const fromApi =
    pickDepositExplorerHref(ex.address) ||
    pickDepositExplorerHref(ex.address_url) ||
    pickDepositExplorerHref(ex.account);
  if (!fromApi) return null;
  if (/\{address\}/i.test(fromApi)) {
    if (!address || address === "—") return null;
    return fromApi.replace(/\{address\}/gi, encodeURIComponent(address));
  }
  return fromApi;
}

export function resolveDepositHistoryTxExplorerHref(explorer, txid) {
  const ex = explorer && typeof explorer === "object" ? explorer : {};
  const fromApi =
    pickDepositExplorerHref(ex.tx) ||
    pickDepositExplorerHref(ex.transaction) ||
    pickDepositExplorerHref(ex.tx_hash_url) ||
    pickDepositExplorerHref(ex.txUrl);
  if (!fromApi) return null;
  if (/\{txid\}/i.test(fromApi)) {
    if (!txid || txid === "—") return null;
    return fromApi.replace(/\{txid\}/gi, encodeURIComponent(txid));
  }
  if (/\{txhash\}/i.test(fromApi)) {
    if (!txid || txid === "—") return null;
    return fromApi.replace(/\{txhash\}/gi, encodeURIComponent(txid));
  }
  return fromApi;
}
